import { Button, ErrorMessage, Screen, Text } from "components"
import React, { useState } from "react"
import { Image, Keyboard, Platform, StatusBar, View } from "react-native"
import { style } from "./styles"
import { useNavigation, useRoute } from "@react-navigation/native"
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from "react-native-confirmation-code-field"
import { Controller, useForm } from "react-hook-form"
import { translate } from "i18n"
import flash from "config/flash"
import Modal from "react-native-modal"
import { useColor } from "app/theme/getThemes"
import { TouchableOpacity } from "react-native-gesture-handler"
import Icon from "@expo/vector-icons/Feather"

export const EditNewPinScreen = () => {
  const navigation = useNavigation<any>()
  const { companyTheme } = useColor()
  const route = useRoute()
  const { routeFrom, phoneNumber, otp } = route?.params
  const [isLoading, setIsLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [navigateAfterClose, setNavigateAfterClose] = useState(false)
  const passwordImage = require("assets/passwordChanged.png")
  const { control, handleSubmit, setValue, watch, clearErrors } = useForm({
    reValidateMode: "onSubmit",
  })

  const pinRef = useBlurOnFulfill({ value: watch("pin"), cellCount: 6 })
  const confirmPinRef = useBlurOnFulfill({ value: watch("confirmPin"), cellCount: 6 })
  const [pinProps, getPinCellOnLayoutHandler] = useClearByFocusCell({
    value: watch("pin"),
    setValue: (e) => setValue("pin", e),
  })
  const [confirmPinProps, getConfirmPinCellOnLayoutHandler] = useClearByFocusCell({
    value: watch("confirmPin"),
    setValue: (e) => setValue("confirmPin", e),
  })

  const onSubmit = async (data: any) => {
    setIsLoading(true)
    await fetch(`${process.env.API_URL}/api/client/resetPassword/${phoneNumber}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        otp_no: otp,
        new_pwd: data?.pin,
        company_code: process.env.COMPANY_CODE,
      }),
    })
      .then(async (res) => {
        setIsLoading(false)
        if (res?.ok) {
          if (routeFrom === "forgetPin") {
            setShowModal(true)
          } else {
            flash("success", translate("editNewPinScreen.updateSuccess"))
          }
        } else {
          flash("error", translate("editNewPinScreen.updateFailed"))
        }
      })
      .catch(() => {
        setIsLoading(false)
        flash("error", translate("editNewPinScreen.updateFailed"))
      })
  }

  return (
    <>
      <Screen onPress={() => Platform.OS !== "web" && Keyboard.dismiss()}>
        <View style={style.container}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginTop: StatusBar.currentHeight,
            }}
          >
            <TouchableOpacity
              style={{
                backgroundColor: companyTheme.first,
                borderRadius: 8,
                paddingHorizontal: 25,
                paddingVertical: 5,
              }}
              onPress={() => navigation.goBack()}
            >
              <Icon name="chevron-left" size={25} color="white" />
            </TouchableOpacity>
          </View>

          <Text style={style.titleText}>{translate("editNewPinScreen.description")}</Text>
          <Controller
            name="pin"
            control={control}
            rules={{
              required: translate("errors.required"),
              minLength: { value: 6, message: translate("errors.pinTooShort") },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <View>
                  <Text style={style.fieldTitle}>{translate("common.pin")}</Text>
                  <CodeField
                    autoFocus={true}
                    ref={pinRef}
                    {...pinProps}
                    onFocus={() => {
                      clearErrors("pin")
                    }}
                    value={value}
                    onChangeText={(e) => {
                      clearErrors("pin")
                      onChange(e)
                    }}
                    cellCount={6}
                    rootStyle={style.rootCodeStyle}
                    keyboardType="number-pad"
                    renderCell={({ index, symbol, isFocused }) => (
                      <Text
                        key={index}
                        style={[
                          style.cell,
                          { borderColor: isFocused && companyTheme.first },
                          error?.message && style.errorCell,
                        ]}
                        onLayout={getPinCellOnLayoutHandler(index)}
                      >
                        {symbol ? "•" : isFocused ? <Cursor /> : null}
                      </Text>
                    )}
                  />
                  {error?.message && (
                    <ErrorMessage customMessage={error?.message} errorType={error?.type} />
                  )}
                </View>
              )
            }}
          />
          <Controller
            name="confirmPin"
            control={control}
            rules={{
              required: translate("errors.required"),
              minLength: { value: 6, message: translate("errors.pinTooShort") },
              validate: {
                value: (value) => watch("pin") === value || translate("errors.samePin"),
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <View>
                  <Text style={style.fieldTitle}>{translate("common.confirmPin")}</Text>
                  <CodeField
                    ref={confirmPinRef}
                    {...confirmPinProps}
                    onFocus={() => {
                      clearErrors("confirmPin")
                    }}
                    value={value}
                    onChangeText={(e) => {
                      clearErrors("confirmPin")
                      onChange(e)
                    }}
                    cellCount={6}
                    rootStyle={style.rootCodeStyle}
                    keyboardType="number-pad"
                    renderCell={({ index, symbol, isFocused }) => (
                      <Text
                        key={index}
                        style={[
                          style.cell,
                          { borderColor: isFocused && companyTheme.first },
                          error?.message && style.errorCell,
                        ]}
                        onLayout={getConfirmPinCellOnLayoutHandler(index)}
                      >
                        {symbol ? "•" : isFocused ? <Cursor /> : null}
                      </Text>
                    )}
                  />
                  {error?.message && (
                    <ErrorMessage customMessage={error?.message} errorType={error?.type} />
                  )}
                </View>
              )
            }}
          />
          <View style={style.buttonContainer}>
            <Button
              loading={isLoading}
              onPress={handleSubmit(onSubmit)}
              title={translate("common.register")}
            />
          </View>
        </View>
      </Screen>
      <Modal
        isVisible={showModal}
        onBackdropPress={() => {
          setShowModal(false)
          setNavigateAfterClose(true)
        }}
        onModalHide={() => {
          if (navigateAfterClose) {
            navigation.reset({ index: 0, routes: [{ name: "auth" }] })
            setNavigateAfterClose(false)
          }
        }}
      >
        <View style={style.modalContainer}>
          <Image source={passwordImage} style={style.modalImage} />
          <View style={style.modalTextContainer}>
            <Text style={style.modalTitleText}>
              {translate("editNewPinScreen.passwordChanged")}
            </Text>
            <Text style={style.modalDescriptionText}>
              {translate("editNewPinScreen.loginAccountWithNewPin")}
            </Text>
            <Button
              title={translate("common.gotIt")}
              onPress={() => {
                setShowModal(false)
                setNavigateAfterClose(true)
              }}
            />
          </View>
        </View>
      </Modal>
    </>
  )
}
